import React from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList } from '@fortawesome/free-solid-svg-icons';
import { describeContributors } from '../../utils/meterContributors';

/**
 * Which meters a figure was calculated from, behind an icon.
 *
 * Naming them on the card face cost a line on every card, and three of them
 * already carry a description and a formula. The names are reference material
 * — wanted when a figure looks wrong, not on every glance — so they sit behind
 * a control instead.
 *
 * The icon still carries the one thing that cannot wait for a click: it turns
 * red when a contributor is silent, because that is why the figure is
 * understated. Hiding that behind a click would undo the point of the feature.
 *
 * The popover is portalled to the body. Both cards it appears on set
 * `overflow: hidden` to clip their coloured rail, which would otherwise cut
 * the popover off at the card edge.
 *
 * @param {Array<{meterId: string, name: string, reporting: boolean}>} contributors
 *   Meters behind the figure, from `metersBehind`.
 * @param {string} title - What the popover is listing, e.g. the card's name.
 */
const ContributorsPopover = ({ contributors = [], title }) => {
  const described = describeContributors(contributors);
  if (!described) return null;

  const silent = contributors.filter((c) => !c.reporting).length;

  const popover = (
    <Popover id={`meters-${title}`} className="ws-pop">
      <Popover.Header as="h4">Measured by</Popover.Header>
      <Popover.Body>
        <ul className="ws-pop-list">
          {contributors.map((meter) => (
            <li
              key={meter.meterId}
              className={meter.reporting ? '' : 'ws-pop-item--out'}
            >
              {meter.name}
              {!meter.reporting && <span> — not reporting</span>}
            </li>
          ))}
        </ul>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      placement="top"
      rootClose
      overlay={popover}
      container={typeof document === 'undefined' ? undefined : document.body}
    >
      <button
        type="button"
        className={`ws-pop-btn${silent ? ' ws-pop-btn--out' : ''}`}
        aria-label={`Meters behind this figure: ${described.detail}`}
      >
        <FontAwesomeIcon icon={faList} />
        <span className="ws-pop-count">{contributors.length}</span>
      </button>
    </OverlayTrigger>
  );
};

export default ContributorsPopover;
